import * as actions from '../../reducer/login.actions'
import {authenticateFactorTwoWithRemote, authenticateWithRemote, fetchPasswordPolicy, fetchSsoInformation, getSessionFromRemote, pingInstance, requestNewCodeFromRemote, requestNewPasswordFromRemote, submitAuditMessage, submitNewPassword} from './login.consumers'
import {codeSendDefaultDelay, loginModes, oneSecondMilliseconds, twoFactorCodePrefix} from './helpers/login.data'
import {checkPasswordPolicyViolation, getSecondsLeft, getTimeWithDelay} from './login.helper'

export const switchLoginMode = mode => ({type: actions.switchLoginMode, mode})

export const updateUsername = value => ({type: actions.updateUsername, value})

export const setUsernameDirty = (isDirty = true) => ({type: actions.setUsernameDirty, isDirty})

export const updatePassword = value => ({type: actions.updatePassword, value})

export const setPasswordDirty = (isDirty = true) => ({type: actions.setPasswordDirty, isDirty})

export const updateConfirmPassword = value => ({type: actions.updateConfirmPassword, value})

export const setConfirmPasswordDirty = (isDirty = true) => ({type: actions.setConfirmPasswordDirty, isDirty})

export const updateSecretCode = code => { // code: AG - {digits here}
  const santitizedCode = `${twoFactorCodePrefix}${code.replace(/\D/g, '')}`
  return ({type: actions.updateSecretCode, value: santitizedCode})
}

export const updateAuditMessage = ({auditMessage}) => ({type: actions.updateAuditMessage, auditMessage})

export const setAuditMessageDirty = () => ({type: actions.setAuditMessageDirty})

export const setSecretCodeDirty = (isDirty = true) => ({type: actions.setSecretCodeDirty, isDirty})

export const setSecretCodeSending = (isSending = true) => ({type: actions.setSecretCodeSending, isSending})

export const setAuthInProgress = (isInProgress = true) => ({type: actions.setAuthInProgress, isInProgress})

export const setAuthFailed = (errorMessage = null) => ({type: actions.setAuthFailed, errorMessage})

export const setAuditSubmitFailed = () => ({type: actions.setAuditSubmitFailed})

export const setExistingPasswordUsed = wasExistingPasswordUsed => ({
  type: actions.setExistingPasswordUsed,
  wasExistingPasswordUsed
})

export const setPasswordPolicy = passwordPolicy => ({type: actions.setPasswordPolicy, passwordPolicy})

export const setSsoInformation = ssoInformation => ({type: actions.setSsoInformation, ssoInformation})

export const setPasswordPolicyPending = (isPending = true) => ({type: actions.setPasswordPolicyPending, isPending})

export const setInstanceAvailable = (isAvailable = true) => ({type: actions.setInstanceAvailable, isAvailable})

export const setInstanceProperties = properties => ({type: actions.setInstanceProperties, properties})

export const authenticate = ({username, password}) => (dispatch => {
  dispatch(setUsernameDirty())
  dispatch(setPasswordDirty())
  if ((username.length > 0) && (password.length > 0)) {
    dispatch(setAuthInProgress())
    authenticateWithRemote({username, password})
  }
})

export const authenticateFactorTwo = ({username, code}) => (dispatch => {
  dispatch(setUsernameDirty())
  dispatch(setSecretCodeDirty())
  if ((username.length > 0) && code.startsWith(twoFactorCodePrefix) && (code.length > twoFactorCodePrefix.length)) {
    dispatch(setAuthInProgress())
    authenticateFactorTwoWithRemote({username, code: code.replace('AG - ', '')})
  }
})

export const requestNewPassword = ({username}) => (dispatch => {
  dispatch(setUsernameDirty())
  if (username.length > 0) {
    dispatch(setAuthInProgress())
    requestNewPasswordFromRemote({username})
      .then(() => dispatch(switchLoginMode(loginModes.forgotPasswordEmailSent)))
  }
})

export const setAuditMessage = ({auditMessage}) => (dispatch => {
  if (auditMessage.length > 0) {
    submitAuditMessage({auditMessage})
      .then(response => {
        if (response.ok) {
          response.text().then(() =>
            window.top.location.replace('/agillicadmin/startpage'))
        }
        else {
          dispatch(setAuditSubmitFailed())
        }
      })
  }
})

export const tickCodeDelayTimer = value => ({type: actions.tickCodeDelayTimer, value})

export const resetSecretCodeSendingDelay = () => ({type: actions.resetSecretCodeSendingDelay})

export const codeSendFailed = message => ({type: actions.codeSendFailed, message})

export const enableRequestingNewCode = () => ({type: actions.enableRequestingNewCode})

export const onUpdateView = ({isMobile}) => ({type: actions.onUpdateView, isMobile})

export const requestNewCode = ({username}) => (dispatch => {
  dispatch(setAuthInProgress())
  dispatch(setSecretCodeSending())
  const startCodeSendTimer = () => {
    const endTime = getTimeWithDelay(codeSendDefaultDelay)
    const codeDelayInterval = setInterval(() => {
      const timeLeft = getSecondsLeft(endTime)
      dispatch(tickCodeDelayTimer(timeLeft))
      if (timeLeft <= 0) {
        dispatch(resetSecretCodeSendingDelay())
        clearInterval(codeDelayInterval)
      }
    }, oneSecondMilliseconds)
  }
  requestNewCodeFromRemote({username})
    .then(response => response.json())
    .then(({status}) => {
      dispatch(setSecretCodeSending(false))
      dispatch(setAuthInProgress(false))
      dispatch(codeSendFailed(status))
      if (status === 'SUCCESS') {
        startCodeSendTimer()
      }
      else if (status === 'CODE_SENDING_FAILED') {
        dispatch(resetSecretCodeSendingDelay())
      }
      else if (status === 'CODE_SENDING_BLOCKED') {
        setTimeout(() => {
          dispatch(enableRequestingNewCode())
        }, codeSendDefaultDelay * oneSecondMilliseconds)
      }
    })
})

export const getPasswordPolicy = () => (dispatch => {
  dispatch(setPasswordPolicyPending())
  fetchPasswordPolicy()
    .then(response => response.json())
    .then(result => dispatch(setPasswordPolicy(result)))
})

export const getSsoInformation = () => (dispatch => {
  fetchSsoInformation()
    .then(response => response.json())
    .then(result => dispatch(setSsoInformation(result)))
})

// eslint-disable-next-line sonarjs/cognitive-complexity
export const setNewPassword = ({username, password, confirmPassword, passwordPolicy, isTFAEnabled, hasTFACookie, TFAChannel}) => (dispatch => {
  dispatch(setPasswordDirty())
  dispatch(setConfirmPasswordDirty())
  if ((username.length > 0) && (password.length > 0) && (password === confirmPassword) && (checkPasswordPolicyViolation({
    password,
    passwordPolicy
  }) === false)) {
    dispatch(setAuthInProgress())
    submitNewPassword({password})
      .then(response => {
        if (response.ok) {
          getSessionFromRemote({username, password}).then(() => {
            if (isTFAEnabled && !hasTFACookie) {
              window.top.location.replace(`/reactlogin/?state=tfa-code&username=${username}&tfachannel=${TFAChannel}`)
            }
            else {
              const redirectURL = (window.localStorage && window.localStorage.getItem('loginRedirectURL')) || '/agillicadmin/startpage'
              if (window.localStorage && window.localStorage.getItem('loginRedirectURL')) {
                window.localStorage.removeItem('loginRedirectURL')
              }
              window.top.location.replace(redirectURL)
            }
          })
        }
        else {
          dispatch(setAuthFailed(''))
          response.json().then(({exists}) => {
            dispatch(setExistingPasswordUsed(exists === 'true'))
          })
        }
      })
  }
})

export const getInstanceProperties = () => (dispatch => {
  pingInstance()
    .then(response => response.json())
    .then(({instanceName, instanceType, troubleshootingInfo}) => { // TODO: Handle timeout
      if (instanceName && instanceType) {
        dispatch(setInstanceProperties({
          name: instanceName,
          type: (instanceType === 'STAGING') ? 'stag' : 'prod',
          troubleshootingInfo: troubleshootingInfo || ''
        }))
      }
      else {
        dispatch(setInstanceAvailable(false))
      }
    })
})

export const requestCode = ({username}) => (dispatch => {
  dispatch(setSecretCodeSending())
  requestNewCodeFromRemote({username})
    .then(response => response.json())
    .then(({status}) => {
      dispatch(setSecretCodeSending(false))
      dispatch(codeSendFailed(status))
      setTimeout(() => {
        dispatch(enableRequestingNewCode())
      }, codeSendDefaultDelay * oneSecondMilliseconds)
    })
})
